/**
 * Embed tokens: short-lived attestations of the signed-in Paradigm user that
 * DIME's web-api verifies against Paradigm's published JWKS and resolves
 * through its account-linking table. A linked identity gets a terminal
 * session without ever seeing Clerk; an unlinked one is refused with
 * `IDENTITY_NOT_LINKED` and the terminal package falls back to Clerk.
 *
 * The token is a 5-minute attestation and is deliberately not cached here —
 * the terminal package's session cache owns freshness (roughly one mint per
 * cold mount, none per remount). Requests carry the ambient Paradigm JWT and
 * hit the env's own REST host, which is what keeps a staging session from
 * ever minting against production.
 */
import { request } from '@paradigm/api/src/fetch-api';

/** Registered target in the backend's `EMBED_TOKEN_TARGETS` (audience + TTL). */
const DIME_EMBED_TOKEN_TARGET = 'dime';

/**
 * Mints an embed token for the DIME Terminal. Resolves `null` when the mint
 * fails, which the terminal package reads as "no silent path" and falls
 * through to Clerk sign-in.
 */
export async function mintDimeEmbedToken(): Promise<string | null> {
  const resp = await request<{ readonly token: string }>({
    method: 'GET',
    url: `/account/embed-token/?target=${DIME_EMBED_TOKEN_TARGET}`,
  });
  return resp.ok ? resp.data.token : null;
}
